import { faTooth } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/index';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@repo/videa-components/src/ui/popover';
import { isToday } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  isIndicatorPreferenceOpen,
  isPreferenceMenuOpen,
} from '../../../app/appHeader/atoms';
import { RecommendationPreferenceModal } from '../../../app/appHeader/practicePreferencesMenu/indicatorPreferences/IndicatorPreferences';
import { selectDateAtom } from '../../../modules/patientList/atoms';
import { QuadrantTooth } from '../../../modules/patientList/patientStrip/patientStripOdontogram/quadrantTooth/quadrantTooth';
import { useGetRecommendationName } from '../../../modules/patientList/patientStrip/patientStripOdontogram/utils';
import { Aligner } from '../../../svgs/Aligner';
import Tooth25Normal from '../../../svgs/odontogram-svgs/components/Tooth25Normal';
import Tooth26Normal from '../../../svgs/odontogram-svgs/components/Tooth26Normal';
import Tooth27Normal from '../../../svgs/odontogram-svgs/components/Tooth27Normal';
import Tooth28Normal from '../../../svgs/odontogram-svgs/components/Tooth28Normal';
import Tooth29Normal from '../../../svgs/odontogram-svgs/components/Tooth29Normal';
import Tooth30Implant from '../../../svgs/odontogram-svgs/components/Tooth30Implant';
import Tooth30Normal from '../../../svgs/odontogram-svgs/components/Tooth30Normal';
import Tooth31Normal from '../../../svgs/odontogram-svgs/components/Tooth31Normal';
import Tooth32Crown from '../../../svgs/odontogram-svgs/components/Tooth32Crown';
import Tooth32Extracted from '../../../svgs/odontogram-svgs/components/Tooth32Extracted';
import Tooth32Extraction from '../../../svgs/odontogram-svgs/components/Tooth32Extraction';
import Tooth32Filling from '../../../svgs/odontogram-svgs/components/Tooth32Filling';
import Tooth32Incipient from '../../../svgs/odontogram-svgs/components/Tooth32Incipient';
import Tooth32Missing from '../../../svgs/odontogram-svgs/components/Tooth32Missing';
import Tooth32Normal from '../../../svgs/odontogram-svgs/components/Tooth32Normal';
import Tooth32RootCanal from '../../../svgs/odontogram-svgs/components/Tooth32RootCanal';
import Tooth32Unknown from '../../../svgs/odontogram-svgs/components/Tooth32Unknown';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';

export const Legend = () => {
  const selectedDate = useAtomValue(selectDateAtom);
  const { trackEvent } = useTrackEvent();
  const { getRecommendationName } = useGetRecommendationName();
  const setIsPracticePreferencesOpen = useSetAtom(isPreferenceMenuOpen);
  const setIsIndicatorSettingsOpen = useSetAtom(isIndicatorPreferenceOpen);

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            data-testid="legend-button"
            onClick={() => {
              trackEvent(mixpanelEvents.huddleLegendClick, {
                date: selectedDate,
                isToday: selectedDate ? isToday(selectedDate) : false,
              });
            }}
          >
            <FontAwesomeIcon icon={faTooth} /> <Trans>Legend</Trans>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="vai-w-fit vai-font-inter">
          <div className="vai-flex vai-items-center vai-justify-between vai-py-2">
            <h3 className="vai-text-sm vai-text-foreground vai-font-medium">
              <Trans>Odontogram Legend</Trans>
            </h3>
            <button
              onClick={() => setIsIndicatorSettingsOpen(true)}
              className="vai-text-xs vai-text-blue-600 hover:vai-text-blue-700 vai-font-medium vai-transition-colors"
            >
              <Trans>View Recommendations Guide</Trans>
            </button>
          </div>
          <h4 className="vai-pt-4 vai-pb-3 vai-text-xs vai-text-gray-700 vai-font-medium vai-tracking-wide vai-uppercase">
            <Trans>Tooth Shapes</Trans>
          </h4>
          <div className="vai-grid vai-grid-cols-2 vai-gap-2">
            <div className="vai-flex vai-flex-row vai-gap-2">
              <Tooth32Normal className="" />
              <span className="vai-text-sm vai-text-gray-800">
                <Trans>No Treatment</Trans>
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2">
              <Tooth32Extraction className="" findingsFill="#94A3B8" />
              <span className="vai-text-sm vai-text-gray-800">
                {getRecommendationName('extraction')}
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2">
              <Tooth32Incipient className="" findingsFill="#94A3B8" />
              <span className="vai-text-sm vai-text-gray-800">
                {getRecommendationName('enamelRegen')}
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2">
              <Tooth32Missing className="" findingsFill="#94A3B8" />
              <span className="vai-text-sm vai-text-gray-800">
                <Trans>No Info</Trans>
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2">
              <Tooth32Filling className="" findingsFill="#94A3B8" />
              <span className="vai-text-sm vai-text-gray-800">
                {getRecommendationName('filling')}
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2">
              <Tooth32Extracted className="" findingsFill="#94A3B8" />
              <span className="vai-text-sm vai-text-gray-800">
                <Trans>Missing</Trans>
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2">
              <Tooth32Crown className="" findingsFill="#94A3B8" />
              <span className="vai-text-sm vai-text-gray-800">
                {getRecommendationName('crown')}
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2">
              <Tooth32Unknown className="" findingsFill="#94A3B8" />
              <span className="vai-text-sm vai-text-gray-800">
                <Trans>Other</Trans>
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2">
              <Tooth32RootCanal className="" findingsFill="#94A3B8" />
              <span className="vai-text-sm vai-text-gray-800">
                {getRecommendationName('rootCanal')}
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2">
              <Tooth30Implant className="" findingsFill="#94A3B8" />
              <span className="vai-text-sm vai-text-gray-800">
                {getRecommendationName('implant')}
              </span>
            </div>
          </div>
          <h4 className="vai-pt-4 vai-pb-3 vai-text-xs vai-text-gray-700 vai-font-medium vai-tracking-wide vai-uppercase">
            {getRecommendationName('perio')}
            <Trans> treatment Opportunity - Quad Outline</Trans>
          </h4>
          <div className="vai-border-purple-700 vai-border-solid vai-border-4 vai-w-fit vai-rounded-md vai-p-2 vai-flex vai-flex-row vai-gap-2">
            <Tooth32Normal className="" />
            <Tooth31Normal className="" />
            <Tooth30Normal className="" />
            <Tooth29Normal className="" />
            <Tooth28Normal className="" />
            <Tooth27Normal className="" />
            <Tooth26Normal className="" />
            <Tooth25Normal className="" />
          </div>
          <h4 className="vai-pt-4 vai-pb-3 vai-text-xs vai-text-gray-700 vai-font-medium vai-tracking-wide vai-uppercase">
            <Trans>Spotlight Visualization</Trans>
          </h4>
          <div className="vai-flex vai-items-center vai-gap-3">
            <div className="vai-relative vai-inline-flex vai-pb-3 vai-pointer-events-none">
              <QuadrantTooth
                currentToothIndex={1}
                isSpotlight
                lastToothIndex={1}
                quadrant="LR"
                toothStatus={{
                  status: 'recommended',
                  toothId: '32',
                  type: 'enamelRegen',
                }}
              />
            </div>

            <p className="vai-text-[12px] vai-text-gray-500 vai-max-w-72">
              <button
                className="vai-text-[#3961E6] vai-cursor-pointer vai-bg-transparent vai-border-none"
                onClick={() => setIsPracticePreferencesOpen(true)}
              >
                <Trans>Click here</Trans>
              </button>{' '}
              <Trans>
                to set the &quot;spotlight&quot; visual for one treatment type.
              </Trans>
            </p>
          </div>
          <h4 className="vai-pt-4 vai-pb-3 vai-text-xs vai-text-gray-700 vai-font-medium vai-tracking-wide vai-uppercase">
            <Trans>Icons</Trans>
          </h4>
          <div className="vai-grid vai-grid-cols-2 vai-gap-2">
            <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              <Aligner />
              <span className="vai-text-sm vai-text-gray-800">
                {getRecommendationName('aligner')}
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              <FontAwesomeIcon
                icon={faPlusCircle}
                className="vai-text-purple-700"
              />

              <span className="vai-text-sm vai-text-gray-800">
                <Trans>More Details</Trans>
              </span>
            </div>
          </div>
          <h4 className="vai-pt-4 vai-pb-3 vai-text-xs vai-text-gray-700 vai-font-medium vai-tracking-wide vai-uppercase">
            <Trans>Colors</Trans>
          </h4>
          <div className="vai-grid vai-grid-cols-2 vai-gap-2">
            <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              <div className="vai-w-4 vai-h-4 vai-rounded-full vai-bg-purple-700" />
              <span className="vai-text-sm vai-text-gray-800">
                <Trans>Opportunity</Trans>
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              <div className="vai-w-4 vai-h-4 vai-rounded-full vai-bg-slate-400" />
              <span className="vai-text-sm vai-text-gray-800">
                <Trans>Scheduled</Trans>
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              <div className="vai-w-4 vai-h-4 vai-rounded-full vai-bg-red-500" />
              <span className="vai-text-sm vai-text-gray-800">
                <Trans>Unscheduled</Trans>
              </span>
            </div>
            <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              <div className="vai-w-4 vai-h-4 vai-rounded-full vai-bg-aqua-400" />
              <span className="vai-text-sm vai-text-gray-800">
                <Trans>Completed</Trans>
              </span>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <RecommendationPreferenceModal />
    </>
  );
};
